import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Icon from 'antd/lib/icon';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import get from 'lodash/get';

import {
  CreateModal,
  Loader,
  showErrorNotification,
  showInfoNotification,
} from 'components';
import { destroyMap, fetchMaps } from 'graphql/queries';
import { createLink, useModal } from 'utils';
import formatRelativeDate from 'utils/formatRelativeDate';

import {
  ActionsColumn,
  ActionsRow,
  BoundaryPill,
  BoundariesList,
  DatasetsList,
  HeaderActions,
  LastUpdatedText,
  MapBody,
  MapContent,
  MapDescription,
  MapHeader,
  MapItem,
  MapDatasetRow,
  MapList,
  MapName,
  MapsPage,
  MapSection,
  MapSections,
  SectionTitle,
  SearchInput,
  SortSelect,
  Title,
  Toolbar,
  ZeroState,
  ZeroStateActions,
  ZeroStateDescription,
  ZeroStateTitle,
} from './style';

const { confirm } = Modal;
const { Option } = Select;

const sortMaps = (maps, sortBy) => {
  const sorted = [...maps];

  if (sortBy === 'NAME') {
    sorted.sort((left, right) => left.name.localeCompare(right.name));
    return sorted;
  }

  sorted.sort((left, right) => (
    Date.parse(right.updatedAt || '') - Date.parse(left.updatedAt || '')
  ));
  return sorted;
};

const boundariesToShowCount = 8;

const normalizeBoundaries = (mapShapes) => {
  const shapesByGroup = mapShapes.reduce((accumulator, mapShape) => {
    const shapeGroup = get(mapShape, 'shape.shapeGroup', null);

    if (!shapeGroup || !shapeGroup.id) {
      accumulator.standalone.push(mapShape);
      return accumulator;
    }

    if (!accumulator.grouped[shapeGroup.id]) {
      accumulator.grouped[shapeGroup.id] = {
        shapeGroup,
        mapShapes: [],
      };
    }

    accumulator.grouped[shapeGroup.id].mapShapes.push(mapShape);

    return accumulator;
  }, { grouped: {}, standalone: [] });

  const collapsedGroupShapes = Object.values(shapesByGroup.grouped).reduce((
    accumulator,
    { shapeGroup, mapShapes: groupedMapShapes },
  ) => {
    const groupShapeIds = get(shapeGroup, 'shapes', []).map(shape => shape.id);
    const includedShapeIds = groupedMapShapes
      .map(item => get(item, 'shape.id', null))
      .filter(Boolean);
    const includesWholeGroup = groupShapeIds.length > 0
      && groupShapeIds.length === includedShapeIds.length
      && groupShapeIds.every(id => includedShapeIds.includes(id));

    if (includesWholeGroup) {
      accumulator.push({
        id: `group-${shapeGroup.id}`,
        name: shapeGroup.name,
      });
      return accumulator;
    }

    groupedMapShapes.forEach(({ id, shape }) => accumulator.push({
      id,
      name: get(shape, 'name', 'Unnamed boundary'),
    }));
    return accumulator;
  }, []);

  return [
    ...collapsedGroupShapes,
    ...shapesByGroup.standalone.map(({ id, shape }) => ({
      id,
      name: get(shape, 'name', 'Unnamed boundary'),
    })),
  ];
};

const Maps = () => {
  const { openModal, ...modalProps } = useModal();
  const [searchValue, setSearchValue] = useState('');
  const [sortBy, setSortBy] = useState('LAST_UPDATED');
  const { error, loading, data } = useQuery(fetchMaps);

  const [runDestroyMap] = useMutation(destroyMap, {
    refetchQueries: [{ query: fetchMaps }],
    onCompleted: (response) => {
      const deletedId = get(response, 'mapDestroy.deletedId', null);

      if (deletedId) {
        showInfoNotification(`Deleted map with id ${deletedId}`);
      }
    },
    onError: (mutationError) => {
      const deletedError = get(mutationError, 'graphQLErrors[0].message', null);

      if (deletedError) {
        showErrorNotification(deletedError);
      }
    },
  });

  const maps = get(data, 'maps', []);

  const visibleMaps = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const filtered = normalizedSearch.length === 0
      ? maps
      : maps.filter(({ name, description }) => (
        `${name || ''} ${description || ''}`.toLowerCase().includes(normalizedSearch)
      ));

    return sortMaps(filtered, sortBy);
  }, [maps, searchValue, sortBy]);
  const hasSearchQuery = searchValue.trim().length > 0;
  const hasNoMaps = maps.length === 0;
  const hasNoVisibleMaps = visibleMaps.length === 0;

  const handleDelete = (map) => {
    confirm({
      title: `Are you sure you want to delete ${map.name}?`,
      okButtonProps: { id: 'confirm-card-delete' },
      onOk() {
        runDestroyMap({
          variables: {
            input: {
              id: map.id,
            },
          },
        });
      },
    });
  };

  return (
    <Loader loading={loading} error={error} variant="cardList">
      <MapsPage>
        <MapHeader>
          <div>
            <Title>Maps</Title>
          </div>
          <HeaderActions>
            <Button
              id="open-main-page-create-modal"
              size="large"
              type="primary"
              onClick={openModal}
            >
              New Map
            </Button>
          </HeaderActions>
        </MapHeader>
        <Toolbar>
          <SearchInput>
            <Input
              allowClear
              prefix={<Icon type="search" />}
              placeholder="Search maps..."
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
            />
          </SearchInput>
          <SortSelect
            value={sortBy}
            onChange={setSortBy}
            suffixIcon={<Icon type="down" />}
          >
            <Option value="LAST_UPDATED">Sort by: Last Updated</Option>
            <Option value="NAME">Sort by: Name</Option>
          </SortSelect>
        </Toolbar>
        <MapList id="cards">
          {hasNoVisibleMaps && (
            <ZeroState>
              <ZeroStateTitle>
                {hasNoMaps ? 'No maps yet' : 'No matching maps'}
              </ZeroStateTitle>
              <ZeroStateDescription>
                {hasNoMaps
                  ? 'Create your first map to get started.'
                  : 'Try a different search or clear your current filter.'}
              </ZeroStateDescription>
              <ZeroStateActions>
                {hasNoMaps ? (
                  <Button
                    id="open-main-page-create-modal-empty"
                    type="primary"
                    onClick={openModal}
                  >
                    New Map
                  </Button>
                ) : (
                  hasSearchQuery && (
                    <Button onClick={() => setSearchValue('')}>
                      Clear Search
                    </Button>
                  )
                )}
              </ZeroStateActions>
            </ZeroState>
          )}
          {visibleMaps.map((map) => {
            const mapDatasets = get(map, 'mapDatasets', []);
            const mapShapes = get(map, 'mapShapes', []);
            const normalizedBoundaries = normalizeBoundaries(mapShapes);
            const visibleBoundaries = normalizedBoundaries.slice(0, boundariesToShowCount);
            const remainingBoundaryCount = normalizedBoundaries.length - visibleBoundaries.length;
            const actionsMenu = (
              <Menu>
                <Menu.Item key={`open-${map.id}`}>
                  <Link to={createLink('edit', map.id)}>Open</Link>
                </Menu.Item>
                <Menu.Item
                  id="delete-card"
                  key={`delete-${map.id}`}
                  onClick={() => handleDelete(map)}
                >
                  Delete
                </Menu.Item>
              </Menu>
            );

            return (
              <MapItem key={map.id}>
                <MapContent>
                  <MapName to={createLink('edit', map.id)}>
                    {map.name}
                  </MapName>
                  {map.description && (
                    <MapDescription>{map.description}</MapDescription>
                  )}
                  {(normalizedBoundaries.length > 0 || mapDatasets.length > 0) && (
                    <MapBody>
                      <MapSections>
                        {normalizedBoundaries.length > 0 && (
                          <MapSection>
                            <SectionTitle>Boundaries</SectionTitle>
                            <BoundariesList>
                              {visibleBoundaries.map(({ id, name }) => (
                                <BoundaryPill key={id}>
                                  {name}
                                </BoundaryPill>
                              ))}
                              {remainingBoundaryCount > 0 && (
                                <BoundaryPill>
                                  {`+ ${remainingBoundaryCount} more`}
                                </BoundaryPill>
                              )}
                            </BoundariesList>
                          </MapSection>
                        )}
                        {mapDatasets.length > 0 && (
                          <MapSection>
                            <SectionTitle>Datasets</SectionTitle>
                            <DatasetsList>
                              {mapDatasets.map(({ id, name, color }) => (
                                <MapDatasetRow key={id}>
                                  <Badge color={color} text={name} />
                                </MapDatasetRow>
                              ))}
                            </DatasetsList>
                          </MapSection>
                        )}
                      </MapSections>
                    </MapBody>
                  )}
                </MapContent>
                <ActionsColumn>
                  <ActionsRow>
                    <LastUpdatedText>
                      {`Updated ${formatRelativeDate(map.updatedAt)}`}
                    </LastUpdatedText>
                    <Dropdown overlay={actionsMenu} trigger={['click']} placement="bottomRight">
                      <Button icon="ellipsis" />
                    </Dropdown>
                  </ActionsRow>
                </ActionsColumn>
              </MapItem>
            );
          })}
        </MapList>
        <CreateModal type="Map" {...modalProps} />
      </MapsPage>
    </Loader>
  );
};

export default Maps;
